import React, { useContext, useEffect } from 'react';

import Paper from '@material-ui/core/Paper';
import { navigate } from '@reach/router';
import moment from 'moment';

import { GlobalContext } from '../../../../store';
import { IAdminEvent, initialEventTemplate } from '../../../../store/admin/events/store';

import DatePicker from '../../../../components/DatePicker';
import { FormConsumer, FormProvider, IFormAttributes } from '../../../../components/Forms';
import Input from '../../../../components/Input';
import { dateFormat, lazyEqual } from '../../../../contrib/utils';
import { EventWizardStepTitle } from '../Components/EventWizardStepTitle/EventWizardStepTitle';
import { useBoolean } from '../../../common/Hooks/useBoolean.hook';
import SetupNewSeasonModal from './Components/SetupNewSeasonModal/SetupNewSeasonModal';
import { useEventFitsInEventsSeasons } from './Hooks/useEventFitsInEventsSeasons.Hook';
import { today } from '../../../../Utils/today';

interface IProps {
  eventId: string;
  clubId: string;
}

const forward = (clubId: string, targetEventId: number) =>
  navigate(`/staff/club/${clubId}/event/${targetEventId}/setup/basic`);

const exit = (clubId: string) => navigate(`/staff/club/${clubId}`);

const EventStart = (props: IProps) => {
  const { clubId, eventId } = props;

  const { actions, adminEvents, seasons } = useContext(GlobalContext);

  const event = eventId === 'new' ? initialEventTemplate : adminEvents[Number(eventId)];

  const [
    displaySetupNewSeasonModal,
    setDisplaySetupNewSeasonModal,
    toggleDisplaySetupNewSeasonModal
  ] = useBoolean(false);

  const eventFitsInEventsSeasons = useEventFitsInEventsSeasons();

  const save = async (newEvent: Partial<IAdminEvent>, noAdvance?: boolean) => {
    // We're adding this because of https://code.talpor.com/club15/frontend/-/issues/898 as we want
    // single round events only
    newEvent.end = moment(newEvent.start)
      .add(1, 'days')
      .format();

    if (!eventFitsInEventsSeasons(newEvent)) {
      setDisplaySetupNewSeasonModal(true);

      return;
    }

    if (lazyEqual(event, newEvent)) {
      if (!(event as any).id) {
        return;
      }
      if (noAdvance) {
        exit(clubId);
      } else {
        forward(clubId, (event as any).id);
      }
    }

    const response = !!(newEvent as any).id
      ? await actions.adminEvents.edit(Number(clubId), newEvent)
      : await actions.adminEvents.create(Number(clubId), newEvent);

    if (response.adminEvents && response.adminEvents.current) {
      const current = response.adminEvents.current;
      if (current.rounds.length) {
        for (const round of current.rounds) {
          actions.adminRoundFormats.get(Number(clubId), current.id, round.id);
        }
      }
    }

    if (!response.errors) {
      // TODO: Fix errors and edge case
      const newEventId = (response as any).adminEvents.current.id;
      if (newEventId) {
        forward(clubId, newEventId);
      }
      if (noAdvance) {
        exit(clubId);
      }
    } else if (response.errors) {
      await actions.notifications.push({
        status: 'unread',
        text: response.errors.events.msg || '',
        type: 'error'
      });
    }
  };

  useEffect(() => {
    const clubSeasons = seasons[Number(clubId)];

    if (clubSeasons && clubSeasons.seasons) {
      const theresAnActiveSeason = clubSeasons.seasons.reduce((isActive: boolean, season) => {
        const seasonStartDate = moment(season.start, dateFormat);
        const seasonEndDate = moment(season.end, dateFormat);

        return isActive || today.isBetween(seasonStartDate, seasonEndDate);
      }, false);

      if (!theresAnActiveSeason) {
        setDisplaySetupNewSeasonModal(true);
      }
    }
  }, [seasons, clubId, dateFormat, today]);

  return event ? (
    <>
      <FormProvider
        defaultValues={event}
        onSubmit={save}
        validations={{
          name: 'required',
          start: 'date'
        }}
      >
        <FormConsumer>
          {({ values, formActions: { setValue }, errors }: IFormAttributes) => (
            <div className="event-wizard-page form event-start">
              <EventWizardStepTitle
                buttonText="SAVE & CONTINUE"
                buttonDisabled={errors.FORM || !(values.name || !values.start)}
                title="GETTING STARTED"
              />

              <div className="container full-width">
                <Paper elevation={1} className="paper">
                  <h3>EVENT NAME</h3>

                  <div className="half1">
                    <Input
                      errorMessage="Event name is required"
                      fullWidth={true}
                      hasError={errors.name}
                      id="event-name"
                      label="EVENT NAME"
                      onChange={setValue('name')}
                      placeholder="TOURNAMENT OF CHAMPIONS"
                      type="text"
                      value={values.name}
                    />
                  </div>
                </Paper>

                <Paper elevation={1} className="paper">
                  <h3>EVENT DATES</h3>

                  <div className="half2">
                    <div className="flex">
                      <DatePicker
                        id="start"
                        label="EVENT START"
                        value={values.start}
                        placeholder="04/13/2019"
                        onChange={setValue('start')}
                        onError={setValue('start')}
                      />

                      {/* We're commenting this because of https://code.talpor.com/club15/frontend/-/issues/898 as we want
                         single round events only */}

                      {/* <div className="big-text">THRU</div>

                      <DatePicker
                        id="ends"
                        value={values.end}
                        label="EVENT ENDS"
                        placeholder="05/13/2019"
                        onChange={setValue('end')}
                        onError={setValue('end')}
                        minDate={moment(values.start).format()}
                        minDateMessage={'Date must not be before start date'}
                      /> */}
                    </div>
                  </div>
                </Paper>
              </div>
            </div>
          )}
        </FormConsumer>
      </FormProvider>

      <SetupNewSeasonModal
        displaySetupNewSeasonModal={displaySetupNewSeasonModal}
        toggleDisplaySetupNewSeasonModal={toggleDisplaySetupNewSeasonModal}
      />
    </>
  ) : (
    <div />
  );
};

export default EventStart;
